import { useEffect, useState } from "react";
import { Button, Empty, Form, Input, Modal, Popconfirm, Table, Typography, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { api } from "@/services/api";

const { Title } = Typography;

interface UserSkill {
  id: string;
  name: string;
  description: string;
  content: string;
  triggers: string[];
  created_at: string;
}

const skillApi = {
  list: () => api.get<UserSkill[]>("/my-skills"),
  create: (data: any) => api.post<UserSkill>("/my-skills", data),
  update: (id: string, data: any) => api.put<UserSkill>(`/my-skills/${id}`, data),
  delete: (id: string) => api.delete<void>(`/my-skills/${id}`),
};

export function UserSkillPage() {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<UserSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UserSkill | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setSkills(await skillApi.list()); }
    catch { setSkills([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (r: UserSkill) => {
    setEditing(r);
    form.setFieldsValue({
      name: r.name, description: r.description,
      content: r.content, triggers: (r.triggers || []).join(", "),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      name: values.name, description: values.description || "",
      content: values.content,
      triggers: values.triggers ? values.triggers.split(",").map((s: string) => s.trim()).filter(Boolean) : [],
    };
    setSaving(true);
    try {
      if (editing) { await skillApi.update(editing.id, payload); }
      else { await skillApi.create(payload); }
      message.success(t("success"));
      setModalOpen(false);
      load();
    } catch (e) { message.error(e instanceof Error ? e.message : t("error")); }
    finally { setSaving(false); }
  };

  const columns = [
    { title: t("my_skills.table.name"), dataIndex: "name", key: "name" },
    { title: t("my_skills.table.description"), dataIndex: "description", key: "desc", ellipsis: true },
    {
      title: t("action"), key: "action", width: 80,
      render: (_: unknown, r: UserSkill) => (
        <span style={{ display: "flex", gap: 4 }}>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(r)} />
          <Popconfirm title={t("my_skills.delete.confirm")} onConfirm={async () => { await skillApi.delete(r.id); load(); }}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "24px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>{t("my_skills.title")}</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          setEditing(null); form.resetFields(); setModalOpen(true);
        }}>{t("my_skills.create")}</Button>
      </div>
      {skills.length === 0 && !loading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Empty description={t("my_skills.empty")} />
        </div>
      ) : (
        <Table dataSource={skills} columns={columns} rowKey="id" loading={loading} size="middle" />
      )}
      <Modal
        title={editing ? t("edit") : t("my_skills.create")}
        open={modalOpen} onOk={handleSubmit} onCancel={() => setModalOpen(false)}
        confirmLoading={saving} destroyOnClose width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t("my_skills.table.name")} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("my_skills.form.description")} name="description">
            <Input placeholder={t("my_skills.form.description")} />
          </Form.Item>
          <Form.Item label={t("my_skills.form.content")} name="content" rules={[{ required: true }]}>
            <Input.TextArea rows={8} placeholder={t("my_skills.form.content_hint")} />
          </Form.Item>
          <Form.Item label={t("my_skills.form.triggers")} name="triggers">
            <Input placeholder={t("my_skills.form.triggers_hint")} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
